import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { postToggleHideReadingHistoryRequest } from "../../cards/api/comicApi";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../../auth/api/authApi";
import { useAccountData } from "../../../hooks/useAccountData";

/**
 * Hook to toggle a comic's visibility in reading history
 * This hook handles only the toggle action, not state management
 */
export function useToggleHideFromReadingHistory() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const accountData = useAccountData();

  const accountHiddenComics = accountData?.hidden_reading_history_comics || [];

  const updateHiddenReadingHistoryComics = useCallback(
    (hiddenComics: number[]) => {
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          data: {
            ...oldData.data,
            payload: {
              ...oldData.data.payload,
              results: {
                ...oldData.data.payload.results,
                hidden_reading_history_comics: hiddenComics,
              },
            },
          },
        };
      });
    },
    [queryClient],
  );

  const mutation = useMutation({
    mutationFn: ({ comicId }: { comicId: number }) => {
      const isCurrentlyHidden = accountHiddenComics.includes(comicId);
      return postToggleHideReadingHistoryRequest(comicId, !isCurrentlyHidden);
    },
    onMutate: async ({ comicId }) => {
      // Optimistically update the cache
      const wasHidden = accountHiddenComics.includes(comicId);
      const newHiddenState = !wasHidden;

      const newHiddenComics = newHiddenState
        ? [...accountHiddenComics, comicId]
        : accountHiddenComics.filter((id) => id !== comicId);

      updateHiddenReadingHistoryComics(newHiddenComics);
    },
    onSuccess: (_, { comicId }) => {
      const wasHidden = accountHiddenComics.includes(comicId);

      toast({
        status: "success",
        title: !wasHidden
          ? t("components.slideInMenu.comicDetailsMenu.comicVisibleOnHistory")
          : t("components.slideInMenu.comicDetailsMenu.comicHiddenFromHistory"),
      });
    },
    onError: (_, { comicId }) => {
      // Revert cache back to original state
      updateHiddenReadingHistoryComics(accountHiddenComics);

      const wasHidden = accountHiddenComics.includes(comicId);
      toast({
        status: "error",
        title: `Unable to ${!wasHidden ? "hide" : "unhide"} comic from reading history`,
      });
    },
  });

  return {
    toggleHideFromReadingHistory: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
